import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { ResearchMcpApi } from '../../apis/research-mcp-api';
import type { ResearchMcpApi as ResearchMcpApiType } from '../../apis/research-mcp-api';
import type { ResearchMcpService } from '../research-mcp-service';
import type {
    McpToolResponse,
    PatentRouteSearch,
    PatentSearchPlanResult,
    PatentTechnologyRouteResult,
    RuntimeGraphViewResult,
    TechnicalDecompositionResult,
} from '../../types';

const DECOMPOSITION_TIMEOUT_MS = 180_000;
const SEARCH_PLAN_TIMEOUT_MS = 180_000;
const ROUTE_TIMEOUT_MS = 300_000;
const GRAPH_VIEW_TIMEOUT_MS = 60_000;

@injectable()
export class ResearchMcpServiceImpl implements ResearchMcpService {
    constructor(
        @inject(ResearchMcpApi) private readonly api: ResearchMcpApiType
    ) {}

    decompose(
        query: string,
        signal?: AbortSignal
    ): Promise<McpToolResponse<TechnicalDecompositionResult>> {
        return this.api.callTool(
            'generate_technical_decomposition_draft',
            { query },
            { signal, timeoutMs: DECOMPOSITION_TIMEOUT_MS }
        );
    }

    createGraphView(
        nodeId: string,
        signal?: AbortSignal
    ): Promise<McpToolResponse<RuntimeGraphViewResult>> {
        return this.api.callTool(
            'create_runtime_graph_view',
            { node_id: nodeId, depth: 3 },
            { signal, timeoutMs: GRAPH_VIEW_TIMEOUT_MS }
        );
    }

    planSearch(
        query: string,
        componentId: string,
        signal?: AbortSignal
    ): Promise<McpToolResponse<PatentSearchPlanResult>> {
        return this.api.callTool(
            'generate_patent_search_plan',
            { query, component_id: componentId },
            { signal, timeoutMs: SEARCH_PLAN_TIMEOUT_MS }
        );
    }

    analyzeRoute(
        componentId: string,
        searches: PatentRouteSearch[],
        signal?: AbortSignal
    ): Promise<McpToolResponse<PatentTechnologyRouteResult>> {
        return this.api.callTool(
            'analyze_patent_technology_route',
            { component_id: componentId, searches },
            { signal, timeoutMs: ROUTE_TIMEOUT_MS }
        );
    }
}
